import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';

/**
 * Catalogue reads. Every server read in the storefront goes through a hook here
 * rather than calling `api` from a component, so a filter shape or a stale time
 * has one definition.
 */

export const useProducts = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: ({ signal }) => api.get('/products', { params: filters, signal }),
  });

/**
 * The shop listing. Cursor-based rather than offset, because an offset shifts as
 * the catalogue changes underneath an infinite scroll and duplicates or skips
 * rows as it does.
 */
export const useProductFeed = (filters = {}) =>
  useInfiniteQuery({
    queryKey: queryKeys.products.list(filters),
    queryFn: ({ pageParam, signal }) =>
      api.get('/products', { params: { ...filters, cursor: pageParam }, signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.meta?.cursor ?? undefined,
    // The count is on every page's meta; taking it from the first keeps the
    // "showing 24 of 44" line stable while later pages load.
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
      items: data.pages.flat(),
      total: data.pages[0]?.meta?.total ?? 0,
    }),
  });

export const useProductFacets = (filters = {}) =>
  useQuery({
    queryKey: queryKeys.products.facets(filters),
    queryFn: ({ signal }) => api.get('/products/facets', { params: filters, signal }),
    staleTime: 2 * 60 * 1000,
    // Keeping the previous facets while the next set loads stops the whole
    // filter rail collapsing and re-expanding on every click.
    placeholderData: (previous) => previous,
  });

export const useProduct = (slug) =>
  useQuery({
    queryKey: queryKeys.products.detail(slug),
    queryFn: ({ signal }) => api.get(`/products/${slug}`, { signal }),
    enabled: Boolean(slug),
  });

export const useCategories = (options = {}) =>
  useQuery({
    queryKey: queryKeys.categories.list(options),
    queryFn: ({ signal }) => api.get('/categories', { params: options, signal }),
    staleTime: 10 * 60 * 1000,
  });

export const useCategory = (slug) =>
  useQuery({
    queryKey: queryKeys.categories.detail(slug),
    queryFn: ({ signal }) => api.get(`/categories/${slug}`, { signal }),
    enabled: Boolean(slug),
  });

/**
 * The store's collections. Read on first paint because the accent tokens and the
 * main navigation are both built from them, so it is cached hard — the list is a
 * handful of rows that changes about never.
 */
export const useCollections = () =>
  useQuery({
    queryKey: queryKeys.collections.list(),
    queryFn: ({ signal }) => api.get('/collections', { signal }),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

/**
 * Every bottle a fragrance can be filled into, with the pack sizes each is
 * offered for. A dozen rows that change when the shop gets new glass.
 */
export const useBottles = () =>
  useQuery({
    queryKey: queryKeys.bottles.all,
    queryFn: ({ signal }) => api.get('/bottles', { signal }),
    staleTime: 10 * 60 * 1000,
  });

/** The combo builder in one read: each set, its bottles, and the fragrances it may hold. */
export const useCombos = () =>
  useQuery({
    queryKey: queryKeys.combos.all,
    queryFn: ({ signal }) => api.get('/combos', { signal }),
    staleTime: 5 * 60 * 1000,
  });

export const useBrands = (options = {}) =>
  useQuery({
    queryKey: queryKeys.brands.list(options),
    queryFn: ({ signal }) => api.get('/brands', { params: options, signal }),
    staleTime: 10 * 60 * 1000,
  });

export const useBrand = (slug) =>
  useQuery({
    queryKey: queryKeys.brands.detail(slug),
    queryFn: ({ signal }) => api.get(`/brands/${slug}`, { signal }),
    enabled: Boolean(slug),
  });

export const useSearchSuggestions = (term) =>
  useQuery({
    queryKey: queryKeys.products.suggest(term),
    queryFn: ({ signal }) => api.get('/search/suggest', { params: { q: term }, signal }),
    enabled: term.trim().length >= 2,
    staleTime: 60 * 1000,
  });

export const useProductReviews = (productId, filters = {}) =>
  useQuery({
    queryKey: queryKeys.products.reviews(productId, filters),
    queryFn: ({ signal }) => api.get(`/products/${productId}/reviews`, { params: filters, signal }),
    enabled: Boolean(productId),
  });
